import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { CustomerLogoCell } from 'src/components/customer/grid/CustomerLogoCell'
import { CustomerOnboardingChip } from 'src/components/customer/shared/CustomerOnboardingChip'
import { CustomerProfileDocumentsChip } from 'src/components/customer/shared/CustomerProfileDocumentsChip'
import { CustomerStatusChip } from 'src/components/customer/shared/CustomerStatusChip'
import type { Customer } from 'src/interfaces/customer'
import { isCustomerInactive, resolveCustomerDisplayName } from 'src/utils/customerList'
import { formatDateOnly, parseUnixTimestamp } from 'src/utils/grid'
import { customerTableHeaderTitles } from './constants'
import { CustomersTableFields } from './enums'

/**
 * Wiring only — every non-trivial value comes from utils/customerList or utils/grid.
 *
 * No Actions column: the whole row is clickable (see CustomersTable's onRowClick), so an
 * Open button would be a second control for the same navigation.
 */
export const customerColumns: Array<GridColDef<Customer>> = [
  {
    field: CustomersTableFields.logoUrl,
    headerName: customerTableHeaderTitles[CustomersTableFields.logoUrl],
    width: 72,
    minWidth: 72,
    sortable: false,
    disableExport: true,
    align: 'center',
    headerAlign: 'center',
    // v8 renders cells in text mode by default; element children are only laid out (and
    // vertically centred) when the column opts into flex display.
    display: 'flex',
    renderCell: (params: GridRenderCellParams<Customer>) => {
      return <CustomerLogoCell customer={params.row} />
    },
  },
  {
    field: CustomersTableFields.displayName,
    headerName: customerTableHeaderTitles[CustomersTableFields.displayName],
    flex: 3,
    minWidth: 200,
    sortable: true,
    // Name identifies the row, so it is never offered for hiding in the manage-columns panel.
    hideable: false,
    // See the note on the logo column: required for the renderCell element to centre vertically.
    display: 'flex',
    valueGetter: (value, row) => {
      return resolveCustomerDisplayName(row)
    },
    renderCell: (params: GridRenderCellParams<Customer>) => {
      return (
        // `h-full` so the text sits on the row's vertical centre — without it the div collapses to
        // its content height and renders against the top of the cell.
        <div css={tw`flex h-full w-full items-center gap-2 overflow-hidden`}>
          <Typography variant="body2" css={tw`truncate`}>
            {resolveCustomerDisplayName(params.row)}
          </Typography>
          {isCustomerInactive(params.row) ? (
            <span css={tw`shrink-0`}>
              <CustomerStatusChip status={params.row.status} />
            </span>
          ) : null}
        </div>
      )
    },
  },
  {
    field: CustomersTableFields.masterContractorOnboardedAt,
    headerName: customerTableHeaderTitles[CustomersTableFields.masterContractorOnboardedAt],
    flex: 2,
    minWidth: 140,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    // v8 renders cells in text mode by default; element children are only laid out (and
    // vertically centred) when the column opts into flex display.
    display: 'flex',
    renderCell: (params: GridRenderCellParams<Customer>) => {
      return <CustomerOnboardingChip customer={params.row} />
    },
  },
  {
    field: CustomersTableFields.profileDocumentsStatus,
    headerName: customerTableHeaderTitles[CustomersTableFields.profileDocumentsStatus],
    flex: 2,
    minWidth: 160,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    // v8 renders cells in text mode by default; element children are only laid out (and
    // vertically centred) when the column opts into flex display.
    display: 'flex',
    renderCell: (params: GridRenderCellParams<Customer>) => {
      return <CustomerProfileDocumentsChip customer={params.row} />
    },
  },
  {
    field: CustomersTableFields.createdAt,
    headerName: customerTableHeaderTitles[CustomersTableFields.createdAt],
    flex: 2,
    minWidth: 140,
    sortable: true,
    type: 'date',
    valueGetter: parseUnixTimestamp,
    valueFormatter: formatDateOnly,
  },
]
